import React from "react";
import { Alert, Badge } from "react-bootstrap";
import { AlertTriangle, Layers } from "react-feather";
import { formatDistanceToNow } from "date-fns";
import {
  IWorkPipelineItem,
  IWorkPipelineOtherList,
  WORK_PIPELINE_STAGE_LABELS,
} from "../../interfaces";

const describe = (o: IWorkPipelineOtherList) =>
  [
    `${o.qaListName}: ${WORK_PIPELINE_STAGE_LABELS[o.stage]}`,
    o.assignedToName ? `claimed by ${o.assignedToName}` : "unclaimed",
    o.devEscalatedAt
      ? `escalated to dev by ${o.devEscalatedByName ?? "a tester"} ${formatDistanceToNow(new Date(o.devEscalatedAt), { addSuffix: true })}`
      : null,
  ]
    .filter(Boolean)
    .join(" · ");

/** The other QA lists' open dev escalations for this domain + geo. */
export const otherListDevEscalations = (
  item: Pick<IWorkPipelineItem, "otherLists"> | null | undefined
) => (item?.otherLists ?? []).filter((o) => !!o.devEscalatedAt);

/** "Also on <list>" when the same domain + geo sits on another QA list; red once that list has it with a dev. */
export const OtherListsTag: React.FC<{
  item: Pick<IWorkPipelineItem, "otherLists" | "qaListName">;
  className?: string;
}> = ({ item, className = "mt-1 me-1" }) => {
  const others = item.otherLists ?? [];
  if (!others.length) return null;
  const withDev = others.filter((o) => !!o.devEscalatedAt);
  const title = [
    `This row is on ${item.qaListName}. The same domain + geo is also on:`,
    ...others.map((o) => `• ${describe(o)}`),
    "Check the other list before escalating, so a fail isn't sent to dev twice.",
  ].join("\n");
  return withDev.length ? (
    <Badge bg="danger" className={className} title={title}>
      <AlertTriangle size={11} className="me-1" />
      With dev on {withDev.map((o) => o.qaListName).join(", ")}
    </Badge>
  ) : (
    <Badge bg="secondary" className={className} title={title}>
      <Layers size={11} className="me-1" />
      Also on {others.map((o) => o.qaListName).join(", ")}
    </Badge>
  );
};

/** Drawer warning: another list already sent this domain + geo to dev. */
export const OtherListsDevAlert: React.FC<{
  item: Pick<IWorkPipelineItem, "otherLists"> | null | undefined;
}> = ({ item }) => {
  const withDev = otherListDevEscalations(item);
  if (!withDev.length) return null;
  return (
    <Alert variant="warning" className="py-2">
      <AlertTriangle size={14} className="me-1" />
      Already with a dev from another QA list —{" "}
      {withDev.map((o) => describe(o)).join("; ")}. Check Dev fixes before
      marking this one Broken.
    </Alert>
  );
};
